import React, {useMemo} from "react";
import Form from "../../base/form";
import {Col, Row} from "reactstrap";
import {InputForm} from "../../base/input/input";
import {DropdownForm} from "../../base/dropdown/dropdown";
import Button from "../../base/button/button";
import * as Yup from "yup";
import {makeRequired, makeValidate} from "mui-rff";
import useIndustry from "../../../hooks/use-industry";
import {TagSearchAbilities, TagShareStatuses, TagVisibilities} from "../../../views/panel/operator/products/tag-management/list";

const formNames = {
    keyword: "keyword",
    visibility: 'visibility',
    searchAbility: 'searchAbility',
    shareStatus: 'shareStatus',
    industryId: 'industryId',
}

const schema = Yup.object().shape({
    [formNames.keyword]: Yup.string().nullable(),
    [formNames.visibility]: Yup.string().nullable(),
    [formNames.searchAbility]: Yup.string().nullable(),
    [formNames.shareStatus]: Yup.string().nullable(),
    [formNames.industryId]: Yup.string().nullable(),
});

const validate = makeValidate(schema);
const required = makeRequired(schema);

const TagManagementSearchSection = ({searchData}) => {
    const {industries, hasNoIndustries} = useIndustry({noCategories: true});
    const initialValues = useMemo(() => ({
        [formNames.keyword]: searchData.filters?.keyword,
        [formNames.visibility]: searchData.filters?.visibility,
        [formNames.searchAbility]: searchData.filters?.searchAbility,
        [formNames.shareStatus]: searchData.filters?.shareStatus,
        [formNames.industryId]: searchData.filters?.industryId,
    }), [searchData.filters]);


    const visibilityOptions = useMemo(() => [
        {
            label: "All",
            value: TagVisibilities.all,
            id: TagVisibilities.all
        },
        {
            label: "Visible for client",
            value: TagVisibilities.visible,
            id: TagVisibilities.visible
        },
        {
            label: "Invisible for client",
            value: TagVisibilities.invisible,
            id: TagVisibilities.invisible
        }
    ], [])

    const searchabilityOptions = useMemo(() => [
        {
            label: "All",
            value: TagSearchAbilities.all,
            id: TagSearchAbilities.all
        },
        {
            label: "Searchable",
            value: TagSearchAbilities.searchable,
            id: TagSearchAbilities.searchable
        },
        {
            label: "Not searchable",
            value: TagSearchAbilities.notSearchable,
            id: TagSearchAbilities.notSearchable
        }
    ], [])
    const statusOptions = useMemo(() => [
        {
            label: "All",
            value: TagShareStatuses.all,
            id: TagShareStatuses.all
        },
        {
            label: "Shared with other partners",
            value: TagShareStatuses.shared,
            id: TagShareStatuses.shared
        },
        {
            label: "Private ",
            value: TagShareStatuses.private,
            id: TagShareStatuses.private
        }
    ], [])

    /**
     * Handles searching over the list of tags based on received values
     * @param values
     */
    const onSubmit = (values) => {
        searchData.setFilters({
            keyword: values[formNames.keyword],
            visibility: values[formNames.visibility],
            searchAbility: values[formNames.searchAbility],
            shareStatus: values[formNames.shareStatus],
            industryId: hasNoIndustries ? undefined : values[formNames.industryId],
        })
    }

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({submitting, values, form}) => {
                const industriesData = [
                    ...(values.industryId ? [{label: "Remove", value: null, id: "all",}] : []),
                    ...industries.map(e => ({label: e.title, value: e.id, id: e.id})),
                ]
                return (
                    <Row className='no-gutters align-items-center justify-content-center mt-4'>
                        <Col xs={12} sm={6} md={8} className={'px-2 mb-3'}>
                            <InputForm
                                onChange={(val) => {
                                    form.change('keyword', val);
                                }}
                                value={values[formNames.keyword] ?? ''}
                                label={'Keywords'}
                                name={formNames.keyword}
                                type={'text'}
                                required={required[formNames.keyword]}
                            />
                        </Col>
                        {
                            !hasNoIndustries && industries?.length > 1 &&
                            <Col xs={12} sm={6} md={4} className={'px-2 mb-3'}>
                                <DropdownForm
                                    label={'Industry'}
                                    name={formNames.industryId}
                                    required={required[formNames.industryId]}
                                    data={industriesData}
                                    renderSelectedValue={(value) => industriesData.find(e => e.value === value)?.label ?? ''}
                                />
                            </Col>
                        }
                        <Col xs={12} sm={6} md={4} xl={3} className='px-2 mb-3'>
                            <DropdownForm
                                label={'Visibility'}
                                name={formNames.visibility}
                                required={required[formNames.visibility]}
                                data={visibilityOptions}
                            />
                        </Col>
                        <Col xs={12} sm={6} md={4} xl={3} className='px-2 mb-3'>
                            <DropdownForm
                                label={'Searchability'}
                                name={formNames.searchAbility}
                                required={required[formNames.searchAbility]}
                                data={searchabilityOptions}
                            />
                        </Col>
                        <Col xs={12} sm={6} md={4} xl={3} className='px-2 mb-3'>
                            <DropdownForm
                                label={'Share Status'}
                                name={formNames.shareStatus}
                                required={required[formNames.shareStatus]}
                                data={statusOptions}
                            />
                        </Col>
                        <Col xs={12} sm={6} md={12} xl={3}
                             className='px-3 d-flex justify-content-center align-items-center mb-3'>
                            <Button
                                disabled={!!submitting || searchData.loading}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='py-3 px-8'
                            >
                                {
                                    submitting
                                        ? "Searching..."
                                        : "Search"
                                }
                            </Button>
                        </Col>
                    </Row>
                );
            }}
        />
    );
}

export default TagManagementSearchSection;
